// Name-based placeholder colours for initials avatars (design: iniHue / iniOf).
// The hue is derived from a normalised, word-order-independent key, so
// "Иванов Иван" and "Иван Иванов" get the same colour.

export type AvatarTone = { background: string; color: string };

// Fallback for a missing / blank name.
export const NEUTRAL_AVATAR_TONE: AvatarTone = { background: "#dfe7f2", color: "#46617f" };

function avatarKey(name: string | null | undefined): string {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/ё/g, "е")
    .split(/\s+/)
    .filter(Boolean)
    .sort()
    .join(" ");
}

function avatarHue(key: string): number {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) % 360;
  return h;
}

export function avatarTone(name: string | null | undefined): AvatarTone {
  const key = avatarKey(name);
  if (!key) return NEUTRAL_AVATAR_TONE;
  const h = avatarHue(key);
  return { background: `hsl(${h} 46% 90%)`, color: `hsl(${h} 44% 36%)` };
}

// Цвет вакансии в списках — от её номера, как в расширении hh (VacancySelect):
// золотой угол разводит соседние номера по кругу оттенков, так что подряд
// идущие вакансии не сливаются. Название тут не годится: у десятка вакансий
// оно одно и то же, а номер у каждой свой.
export function vacancyTone(number: number): AvatarTone {
  const hue = Math.round((Math.abs(Number(number) || 0) * 137.508) % 360);
  return {
    background: `radial-gradient(circle at 28% 22%, hsl(${hue} 92% 94%), hsl(${hue} 66% 79%))`,
    color: `hsl(${hue} 62% 30%)`,
  };
}

// First letters of the first two words of the original name. A word gives its
// first letter or digit, not its first character: «Завод «Сокол»» is «ЗС», not
// «З«»; a word with neither (a dash, a lone quote) is skipped.
export function initialsOf(name: string | null | undefined): string {
  if (!name) return "?";
  const letters = name
    .trim()
    .split(/\s+/)
    .map((word) => /[\p{L}\p{N}]/u.exec(word)?.[0] ?? "")
    .filter(Boolean)
    .slice(0, 2);
  return letters.map((letter) => letter.toUpperCase()).join("") || "?";
}
